import { Redirect, useSegments, type Href } from "expo-router";
import type { PropsWithChildren } from "react";
import { useStartup } from "../hooks/useStartup";
import { StartupStateScreen } from "./StartupStateScreen";

export function WebStartupGate({ children, navigatorGroups = false }: PropsWithChildren<{ navigatorGroups?: boolean }>) {
  const { status, retry } = useStartup();
  const segments = useSegments();
  // Auth owns its own routing. Keep this gate outside the user tabs so standalone
  // deep links wait for persistence restoration too. It is not an admin layout.
  if (segments[0] === "(auth)") return children;
  // Legal and account-deletion pages must remain reachable without a session.
  if (segments[0] === "privacy" || segments[0] === "delete-account") return children;
  // Control Center owns Firebase Auth + membership startup independently from
  // the consumer profile/onboarding state.
  if ((segments[0] as string | undefined) === "control") return children;
  // Mount the tabs navigator immediately so Expo Router can hydrate its initial
  // history. The same gate protects each tab's screen content below that navigator.
  if (navigatorGroups && segments[0] === "(tabs)") return children;
  if (status === "loading") return <StartupStateScreen mode="loading" />;
  if (status === "signed_out") return <Redirect href="/(auth)/login" />;
  if (status === "email_verification_required") return <Redirect href={"/(auth)/verify-email" as Href} />;
  if (status === "onboarding_required") return <Redirect href="/(auth)/onboarding" />;
  if (status === "offline" || status === "recoverable_error") {
    return <StartupStateScreen mode="error" offline={status === "offline"} onRetry={retry} />;
  }
  return children;
}
